import React from 'react';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import Joyride, { Styles as JoyrideStyles } from 'react-joyride';

import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { Link, Route, Router, Switch, useHistory, useLocation, useParams } from 'react-router-dom';

import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { FormSubmitEvent } from 'ui/base/types';
import { PasswordStore } from 'ui/base/input';
import { BusyStore } from 'ui/base/button';
import { BackButton } from 'ui/base/button/back_button';
import { HistoryModalStore, Modal } from 'ui/base/modal';
import { StandardPage } from 'ui/base/page/standard_page';

import { LabeledInput, PasswordInput } from 'ui/public/input';
import { ActiveButton, DefaultButton, LinkButton } from 'ui/public/button';
import { ErrorMessage } from 'ui/public/error_message/error_message';

import { PAYMENT_REDIRECT, PLATFORM } from 'services/base/config';
import { GlobalLocaleStore, localeToLanguageCode } from 'services/base/i18n';
import { IAccountsDomain, InvoiceStripe, Pangolin, PaymentType } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';
import { PaymentsService } from 'services/accounts/payments_service';
import { getPlanFirewallPlanActivePackage, getPlanFirewallStripeItemLabel } from 'services/accounts/logic/plans/plan_firewall';
import { getPaymentMethodInfo, getPaymentMethodName, getPaymentMethodPlatform } from 'services/accounts/logic/payments/payments';
import { flattenInvoiceItems, totalInvoiceItems } from 'services/accounts/logic/invoices/invoices';

import { Redirect } from 'pages/core/redirect/redirect';

import { formatPrice } from 'assets/enums/currencies/currencies';

import { PlanItemFirewall } from './ui/plan_item/plan_item_firewall';

import baseStyles from './manage_plans.module.css';
import Messages from './manage_plans_messages';

import heroIconImageWarningMidnight from './assets/heroes/warning/dark.png';
import heroIconImageWarningDaylight from './assets/heroes/warning/light.png';
import heroIconImageStatusGoodMidnight from './assets/heroes/status_good/dark.png';
import heroIconImageStatusGoodDaylight from './assets/heroes/status_good/light.png';
import heroIconImageStatusWarnMidnight from './assets/heroes/status_warn/dark.png';
import heroIconImageStatusWarnDaylight from './assets/heroes/status_warn/light.png';

import fieldIconImagePaymentMidnight from './assets/fields/payment/dark.png';
import fieldIconImagePaymentDaylight from './assets/fields/payment/light.png';
import fieldIconImageInvoiceMidnight from './assets/fields/invoice/dark.png';
import fieldIconImageInvoiceDaylight from './assets/fields/invoice/light.png';
import fieldIconImageStatusGoodMidnight from './assets/fields/status_good/dark.png';
import fieldIconImageStatusGoodDaylight from './assets/fields/status_good/light.png';
import fieldIconImageStatusWarnMidnight from './assets/fields/status_warn/dark.png';
import fieldIconImageStatusWarnDaylight from './assets/fields/status_warn/light.png';

import pangolinGlowImage from './assets/pangolin_glow.png';

const themedJoyrideStyles: ThemedStyles<JoyrideStyles> = {
  [Theme.DAYLIGHT]: {
    tooltip: {
      paddingLeft:30,
      paddingRight:30,
    },
    options: {
      arrowColor: '#ffc107',
      backgroundColor: '#ffc107',
      textColor: '#000',
      width: 300,
    },
  },
  [Theme.MIDNIGHT]: {
    tooltip: {
      paddingLeft:30,
      paddingRight:30,
    },
    options: {
      arrowColor: '#2d539d',
      backgroundColor: '#2d539d',
      textColor: '#fff',
      width: 300,
    },
  },
};

type ManagePlansStyles = {
  header: string;
  headerItem: string;
  headerText: string;
  headerButton: string;
  headerButtonIcon: string;

  hero: string;
  heroIcon: string;
  heroGlow: string;
  heroHead: string;
  heroTitle: string;
  heroText: string;
  heroDetail: string;

  content: string;

  field: string;
  fieldPart: string;
  fieldIcon: string;
  fieldTextLeft: string;
  fieldTextRight: string;
  fieldCharge: string;
  fieldAmount: string;
  fieldInfo: string;
  fieldInfoLong: string;

  modalContainer: string;
  modalContainerBig: string;
  modalContainerLarge: string;
  modalContainerLargest: string;
  modalContainerPopOut: string;
  modalHeaderRow: string;
  modalContentRow: string;
  modalCloseIcon: string;

  section: string;
  sectionTitle: string;

  clickable: string;

  item: string;

  link: string;

  open: string;
  delete: string;

  heroIconImageWarning: string;
  heroIconImageStatusGood: string;
  heroIconImageStatusWarn: string;

  fieldIconImagePayment: string;
  fieldIconImageInvoice: string;
  fieldIconImageStatusGood: string;
  fieldIconImageStatusWarn: string;
};

const themedStyles: ThemedStyles<ManagePlansStyles> = {
  [Theme.DAYLIGHT]: {
    header: baseStyles.header,
    headerItem: baseStyles.headerItem,
    headerText: baseStyles.daylightHeaderText,
    headerButton: baseStyles.headerButton,
    headerButtonIcon: baseStyles.daylightHeaderButtonIcon,

    hero: baseStyles.hero,
    heroIcon: baseStyles.heroIcon,
    heroGlow: baseStyles.herGlow,
    heroHead: baseStyles.heroHead,
    heroTitle: baseStyles.heroTitle,
    heroText: baseStyles.heroText,
    heroDetail: baseStyles.heroDetail,

    content: baseStyles.content,

    field: baseStyles.field,
    fieldPart: baseStyles.fieldPart,
    fieldIcon: baseStyles.fieldIcon,
    fieldTextLeft: baseStyles.fieldTextLeft,
    fieldTextRight: baseStyles.fieldTextRight,
    fieldCharge: baseStyles.fieldCharge,
    fieldAmount: baseStyles.fieldAmount,
    fieldInfo: baseStyles.fieldInfo,
    fieldInfoLong: baseStyles.fieldInfoLong,

    modalContainer: baseStyles.modalContainer,
    modalContainerBig: baseStyles.modalContainerBig,
    modalContainerLarge: baseStyles.modalContainerLarge,
    modalContainerLargest: baseStyles.modalContainerLargest,
    modalContainerPopOut: baseStyles.modalContainerPopOut,
    modalHeaderRow: baseStyles.modalHeaderRow,
    modalContentRow: baseStyles.modalContentRow,
    modalCloseIcon: baseStyles.daylightModalCloseIcon,

    section: baseStyles.section,
    sectionTitle: baseStyles.daylightSectionTitle,

    clickable: baseStyles.clickable,

    item: baseStyles.daylightItem,

    link: baseStyles.link,

    open: baseStyles.daylightOpen,
    delete: baseStyles.daylightDelete,

    heroIconImageWarning: heroIconImageWarningDaylight,
    heroIconImageStatusGood: heroIconImageStatusGoodDaylight,
    heroIconImageStatusWarn: heroIconImageStatusWarnDaylight,

    fieldIconImagePayment: fieldIconImagePaymentDaylight,
    fieldIconImageInvoice: fieldIconImageInvoiceDaylight,
    fieldIconImageStatusGood: fieldIconImageStatusGoodDaylight,
    fieldIconImageStatusWarn: fieldIconImageStatusWarnDaylight,
  },
  [Theme.MIDNIGHT]: {
    header: baseStyles.header,
    headerItem: baseStyles.headerItem,
    headerText: baseStyles.midnightHeaderText,
    headerButton: baseStyles.headerButton,
    headerButtonIcon: baseStyles.midnightHeaderButtonIcon,

    hero: baseStyles.hero,
    heroIcon: baseStyles.heroIcon,
    heroGlow: baseStyles.heroGlow,
    heroHead: baseStyles.heroHead,
    heroTitle: baseStyles.heroTitle,
    heroText: baseStyles.heroText,
    heroDetail: baseStyles.heroDetail,

    content: baseStyles.content,

    field: baseStyles.field,
    fieldPart: baseStyles.fieldPart,
    fieldIcon: baseStyles.fieldIcon,
    fieldTextLeft: baseStyles.fieldTextLeft,
    fieldTextRight: baseStyles.fieldTextRight,
    fieldCharge: baseStyles.fieldCharge,
    fieldAmount: baseStyles.fieldAmount,
    fieldInfo: baseStyles.fieldInfo,
    fieldInfoLong: baseStyles.fieldInfoLong,

    modalContainer: baseStyles.modalContainer,
    modalContainerBig: baseStyles.modalContainerBig,
    modalContainerLarge: baseStyles.modalContainerLarge,
    modalContainerLargest: baseStyles.modalContainerLargest,
    modalContainerPopOut: baseStyles.modalContainerPopOut,
    modalHeaderRow: baseStyles.modalHeaderRow,
    modalContentRow: baseStyles.modalContentRow,
    modalCloseIcon: baseStyles.midnightModalCloseIcon,

    section: baseStyles.section,
    sectionTitle: baseStyles.midnightSectionTitle,

    clickable: baseStyles.clickable,

    item: baseStyles.midnightItem,

    link: baseStyles.link,

    open: baseStyles.midnightOpen,
    delete: baseStyles.midnightDelete,

    heroIconImageWarning: heroIconImageWarningMidnight,
    heroIconImageStatusGood: heroIconImageStatusGoodMidnight,
    heroIconImageStatusWarn: heroIconImageStatusWarnMidnight,

    fieldIconImagePayment: fieldIconImagePaymentMidnight,
    fieldIconImageInvoice: fieldIconImageInvoiceMidnight,
    fieldIconImageStatusGood: fieldIconImageStatusGoodMidnight,
    fieldIconImageStatusWarn: fieldIconImageStatusWarnMidnight,
  },
};

type MoreInvoiceViewModalProps = MoreBaseModalProps;
const MoreInvoiceViewModal = observer(({ styles, accounts, modal }: MoreInvoiceViewModalProps) => {
  const { invoiceID } = useParams<{ invoiceID: string }>();

  const invoice = accounts.invoices.get(invoiceID);

  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  const nestNodeUnderList = (child: React.ReactNode, depth: number, d = 0) => {
    return d === depth ? child : <li>{nestNodeUnderList(child, depth, d + 1)}</li>;
  };

  return (
    <div className={classNames(styles.modalContainerLarge, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.invoiceViewName()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        {invoice && (
          <div className={styles.modalContentRow}>
            <div className={styles.section}>
              <div className={styles.hero}>
                {invoice.settled_at ? (
                  <img className={styles.heroIcon} src={styles.heroIconImageStatusGood} alt='Good Icon' />
                ) : (
                  <img className={styles.heroIcon} src={styles.heroIconImageStatusWarn} alt='Warning Icon' />
                )}
                <div className={styles.heroHead}>
                  {formatPrice(invoice.currency, Math.max(totalInvoiceItems(invoice), 0), true)}
                </div>
                <div className={styles.heroTitle}>
                  {getPlanFirewallStripeItemLabel(invoice.label, locale)}
                </div>
                <div className={styles.heroDetail}>
                  {invoiceID}
                </div>
              </div>
            </div>
            <div className={styles.section}>
              <div className={styles.field}>
                <div className={styles.fieldPart}>
                  <div className={classNames(styles.field, styles.item)}>
                    <div className={styles.fieldTextLeft} >
                      {invoice.created_at.toLocaleString(locale, format)}
                      <div className={styles.fieldInfo}>
                        {Messages.invoiceViewFieldCreatedDate()}
                      </div>
                    </div>
                    <img className={styles.fieldIcon} src={styles.fieldIconImageStatusWarn} alt='Warning Icon' />
                  </div>
                  <div className={classNames(styles.field, styles.item)}>
                    <div className={styles.fieldTextLeft} >
                      {invoice.settled_at ? invoice.settled_at.toLocaleString(locale, format)
                                          : Messages.invoiceViewStatusPending()}
                      <div className={styles.fieldInfo}>
                        {Messages.invoiceViewFieldCompletedDate()}
                      </div>
                    </div>
                    <img className={styles.fieldIcon} src={styles.fieldIconImageStatusGood} alt='Good Icon' />
                  </div>
                  {invoice.payment && (
                    <div className={classNames(styles.field, styles.item)}>
                      <div className={styles.fieldTextLeft} >
                        {getPaymentMethodName(invoice.payment)}
                        <div className={styles.fieldInfo}>
                          {Messages.invoiceViewFieldPayment()}
                        </div>
                      </div>
                      <img className={styles.fieldIcon} src={styles.fieldIconImagePayment} alt='Payment Icon' />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.section}>
              {flattenInvoiceItems(invoice).map((item, index) => (
                <div className={classNames(styles.field, styles.item)} key={`item-${index}`}>
                  <div className={styles.fieldCharge}>
                    {nestNodeUnderList(getPlanFirewallStripeItemLabel(item.label, locale), item.depth)}
                  </div>
                  <div className={styles.fieldAmount}>
                    {formatPrice(invoice.currency, item.amount, true)}
                  </div>
                </div>
              ))}
              <div className={classNames(styles.field, styles.item)}>
                <div className={styles.fieldCharge}>
                  <strong>{Messages.invoiceViewFieldAmountTotal()}</strong>
                </div>
                <div className={styles.fieldAmount}>
                  {formatPrice(invoice.currency, totalInvoiceItems(invoice), true)}
                </div>
              </div>
              <div className={classNames(styles.field, styles.item)}>
                <div className={styles.fieldCharge}>
                  <strong>{Messages.invoiceViewFieldAmountDue()}</strong>
                </div>
                <div className={styles.fieldAmount}>
                  {formatPrice(invoice.currency, Math.max(totalInvoiceItems(invoice), 0), true)}
                </div>
              </div>
            </div>
            {(invoice.type === PaymentType.STRIPE) && (invoice as InvoiceStripe).link && (
              <div className={styles.section}>
                <a className={styles.link}
                   target='_blank' rel='noopener noreferrer'
                   href={(invoice as InvoiceStripe).link}>
                  {Messages.invoiceViewActionOpenStripe()}
                </a>
              </div>
            )}
            <DefaultButton type='button' onClick={modal.history.goBack}>
              {Messages.actionBack()}
            </DefaultButton>
          </div>
        )}
      </StandardPage>
    </div>
  );
});

type MoreInvoiceListModalProps = MoreBaseModalProps;
const MoreInvoiceListModal = observer(({ styles, accounts, modal, root }: MoreInvoiceListModalProps) => {
  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
  };

  return (
    <div className={classNames(styles.modalContainerLargest, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.invoiceListName()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <div className={styles.section}>
            {accounts.invoices.sorted((a, b) => b.created_at.getTime() - a.created_at.getTime()).map(invoice => (
              <Link key={invoice.id} to={`${root}/invoices/${invoice.id}`}
                    className={classNames(styles.field, styles.item, styles.clickable)}>
                <div className={styles.fieldTextLeft} style={{ flex: 2 }}>
                  {invoice.created_at.toLocaleDateString(locale, format)}
                  <div className={styles.fieldInfo}>
                    {Messages.invoiceListFieldDate()}
                  </div>
                </div>
                <div className={styles.fieldTextLeft} >
                  {formatPrice(invoice.currency, Math.max(totalInvoiceItems(invoice), 0), true)}
                  <div className={styles.fieldInfo}>
                    {Messages.invoiceListFieldAmount()}
                  </div>
                </div>
                <div className={styles.fieldTextLeft} >
                  {!invoice.settled_at ? Messages.invoiceListStatusPending()
                                       : Messages.invoiceListStatusDone()}
                  <div className={styles.fieldInfo}>
                    {Messages.invoiceListFieldStatus()}
                  </div>
                </div>
                <div className={styles.open}/>
              </Link>
            ))}
          </div>
          <DefaultButton type='button' onClick={modal.history.goBack}>
            {Messages.actionBack()}
          </DefaultButton>
        </div>
      </StandardPage>
    </div>
  );
});

type MorePaymentDeleteModalProps = MoreBaseModalProps;
const MorePaymentDeleteModal = observer(({ styles, accounts, modal, root }: MorePaymentDeleteModalProps) => {
  const { paymentID } = useParams<{ paymentID: string }>();

  const [payment] = React.useState(() => accounts.payments.get(paymentID)!);

  const [busy] = React.useState(new BusyStore());
  const [passwordStore] = React.useState(new PasswordStore(''));
  const [passwordError, setPasswordError] = React.useState(' ');

  const disabled = passwordStore.value.length === 0;

  const onSubmit = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();

    const result = await busy.perform(async() => {
      try {
        modal.freezeModal();
        return await PaymentsService.delete(
          accounts, payment,
          passwordStore.value,
        );
      } finally {
        modal.unfreezeModal();
      }
    });

    if (result.success) {
      runInAction(() => {
        while (modal.history.position > 1) modal.history.goBack();
        modal.history.replace(`${root}/payments`);
      });
    } else if (result.status === 403) {
      // Display password error
      setPasswordError(Messages.paymentDeleteErrorPassword());
    } else {
      // Display generic error
      setPasswordError(Messages.paymentDeleteErrorGeneric());
    }
  }, [modal, accounts, root, busy, payment, passwordStore]);

  return (
    <div className={classNames(styles.modalContainerBig, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.paymentDeleteName()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <form className={styles.modalContentRow} onSubmit={onSubmit}>
          <div className={styles.hero}>
            <img className={styles.heroIcon} src={styles.heroIconImageWarning} alt='Warning Icon' />
            <div className={styles.heroTitle}>
              <ReactMarkdown>
                {Messages.paymentDeleteHeroTitle(getPaymentMethodName(payment))}
              </ReactMarkdown>
            </div>
            <p className={styles.heroText}>
              {Messages.paymentDeleteHeroRefunds()}
              <br/><br/>
              <strong>
                {Messages.paymentDeleteHeroPermanent()}
              </strong>
              <br/><br/>
              {Messages.paymentDeleteHeroPassword()}
            </p>
          </div>
          <LabeledInput label={Messages.inputPassword()}>
            <PasswordInput
              store={passwordStore}
              name='password'
              id='password'
            />
          </LabeledInput>
          <ErrorMessage message={passwordError}/>
          <ActiveButton busy={busy} disabled={disabled}>
            {Messages.actionConfirm()}
          </ActiveButton>
          <DefaultButton type='button' disabled={busy.value} onClick={modal.history.goBack}>
            {Messages.actionBack()}
          </DefaultButton>
        </form>
      </StandardPage>
    </div>
  );
});

type MorePaymentInputCardProps = MoreBaseModalProps & {
  payment_id: string;
  payment_name: string;
  payment_platform: string;
  payment_icon: string;
  payment_info: string;
};
const MorePaymentInputCard = observer(({
  styles,
  root,
  payment_id,
  payment_name,
  payment_platform,
  payment_icon,
  payment_info,
} : MorePaymentInputCardProps) => {
  return (
    <div className={classNames(styles.field, styles.item)}>
      <img className={styles.fieldIcon} src={payment_icon} alt='Payment Icon' />
      <div className={styles.fieldTextLeft} style={{ flex: 2 }}>
        {payment_name}
        <span className={styles.fieldInfo}>
          {payment_info}
        </span>
      </div>
      <div className={styles.fieldTextRight}>
        <span className={styles.fieldInfo}>
          {Messages.labelPayment(payment_platform)}
        </span>
        &nbsp;
      </div>
      <Link to={`${root}/payments/${payment_id}/delete`}>
        <div className={styles.delete}/>
      </Link>
    </div>
  );
});

type MorePaymentListModalProps = MoreBaseModalProps;
const MorePaymentListModal = observer((props: MorePaymentListModalProps) => {
  const { styles, accounts, modal } = props;
  return (
    <div className={classNames(styles.modalContainer, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.paymentListName()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <div className={styles.section}>
            {
              accounts.payments.map((payment, payment_id) => (
                <MorePaymentInputCard
                  key={payment_id}
                  payment_id={payment_id}
                  payment_name={getPaymentMethodName(payment)}
                  payment_platform={getPaymentMethodPlatform(payment)}
                  payment_icon={styles.fieldIconImagePayment}
                  payment_info={getPaymentMethodInfo(payment)}
                  {...props}
                />
              ))
            }
          </div>
          <DefaultButton type='button' onClick={modal.history.goBack}>
            {Messages.actionBack()}
          </DefaultButton>
        </div>
      </StandardPage>
    </div>
  );
});

type MoreHomeModalProps = MoreBaseModalProps;
const MoreHomeModal = observer(({ styles, modal, root }: MoreHomeModalProps) => {
  return (
    <div className={classNames(styles.modalContainer, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.moreSettingsName()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <div className={styles.section}>
            <Link className={classNames(styles.field, styles.item)} to={`${root}/payments`}>
              <img className={styles.fieldIcon} src={styles.fieldIconImagePayment} alt='Payment Icon' />
              <div className={styles.fieldTextRight}>
                {Messages.paymentListName()}
                <div className={styles.fieldInfoLong}>
                  {Messages.paymentListInfo()}
                </div>
              </div>
              <div className={styles.open}/>
            </Link>
            <Link className={classNames(styles.field, styles.item)} to={`${root}/invoices`}>
              <img className={styles.fieldIcon} src={styles.fieldIconImageInvoice} alt='Invoice Icon' />
              <div className={styles.fieldTextRight}>
                {Messages.invoiceListName()}
                <div className={styles.fieldInfoLong}>
                  {Messages.invoiceListInfo()}
                </div>
              </div>
              <div className={styles.open}/>
            </Link>
          </div>
          <DefaultButton type='button' onClick={modal.history.goBack}>
            {Messages.actionBack()}
          </DefaultButton>
        </div>
      </StandardPage>
    </div>
  );
});

export type MoreBaseModalProps = {
  styles: ManagePlansStyles;
  accounts: IAccountsDomain;
  pangolin: Pangolin;
  modal: HistoryModalStore;
  root: string;
};

type MoreModalProps = MoreBaseModalProps;

const MoreModal = observer((props: MoreModalProps) => {
  const { modal, root } = props;
  return (
    <Modal store={modal} onBackdropClick={modal.history.goBack}>
      <Router history={modal.history}>
        <Switch>
          <Route path={`${root}/home`}>
            <MoreHomeModal {...props} />
          </Route>
          <Route path={`${root}/payments/:paymentID/delete`}>
            <MorePaymentDeleteModal {...props} />
          </Route>
          <Route path={`${root}/payments`}>
            <MorePaymentListModal {...props} />
          </Route>
          <Route exact path={`${root}/invoices/:invoiceID`}>
            <MoreInvoiceViewModal {...props} />
          </Route>
          <Route path={`${root}/invoices`}>
            <MoreInvoiceListModal {...props} />
          </Route>
        </Switch>
      </Router>
    </Modal>
  );
});

export const BaseManagePlansDisabled = () => {
  const history = useHistory();
  const styles = useStyles(themedStyles);
  return (
    <StandardPage>
      <div className={styles.header}>
        <div className={styles.headerItem}>
          <BackButton/>
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.hero}>
          <img className={styles.heroGlow} src={pangolinGlowImage} alt='Pangolin Icon' />
          <div className={styles.heroTitle}>
            {Messages.disabledHeroTitle()}
          </div>
          <p className={styles.heroDetail}>
            {Messages.disabledHeroNotice()}
            <br/><br/>
            {Messages.disabledHeroAction()}
          </p>
          <ActiveButton onClick={() => window.open('https://console.pangolinsecured.com/')}>
            {Messages.actionOpen()}
          </ActiveButton>
          <div className={styles.content}>
            <LinkButton onClick={history.goBack}>
              {Messages.disabledActionBackLabel()}
            </LinkButton>
          </div>
        </div>
      </div>
    </StandardPage>
  );
};

type BaseManagePlansDefaultProps = {
  accounts: IAccountsDomain;
  pangolin: Pangolin;
};
export const BaseManagePlansDefault = observer(({ accounts, pangolin }: BaseManagePlansDefaultProps) => {
  const location = useLocation();
  const styles = useStyles(themedStyles);
  const joyride = useStyles(themedJoyrideStyles);

  const root = `${location.pathname}/more`;

  const plan = pangolin.plans.firewall;
  const planActivePackage = getPlanFirewallPlanActivePackage(plan);
  const planActive = !!planActivePackage;

  const [modal] = React.useState(() => new HistoryModalStore(false));

  React.useEffect(() => { return modal.handleGoBack(); }, [modal]);

  const onShowModal = React.useCallback(() => modal.showModalAt(`${root}/home`), [modal, root]);

  return (
    <>
      {!planActive && !PAYMENT_REDIRECT && (
        <Joyride steps={[{
          target: '#plan-item-firewall',
          content: (
            <ReactMarkdown>
              {Messages.noPlanMessage()}
            </ReactMarkdown>
          ),
          disableBeacon: true,
          disableScrolling: true,
          disableScrollParentFix: true,
          hideCloseButton: true,
          hideBackButton: true,
          hideFooter: true,
          placement: 'bottom-start',
          floaterProps: { disableAnimation: true },
          styles: joyride,
        }]} />
      )}
      <MoreModal
        styles={styles}
        accounts={accounts}
        pangolin={pangolin}
        modal={modal}
        root={root}
      />
      <StandardPage>
        <div className={styles.header}>
          <div className={styles.headerItem}>
            <BackButton/>
            <div className={styles.headerText}>
              {Messages.title()}
            </div>
          </div>
          <div className={styles.headerItem}>
            <button type='button' className={styles.headerButton}
                    onClick={onShowModal}>
              <div className={styles.headerButtonIcon}/>
            </button>
          </div>
        </div>
        <div className={styles.content}>
          <PlanItemFirewall
            accounts={accounts}
            pangolin={pangolin}
            plan={pangolin.plans.firewall}
            id='plan-item-firewall'
          />
        </div>
      </StandardPage>
    </>
  );
});

type BaseManagePlansProps = {
  accounts: IAccountsDomain;
  pangolin: Pangolin;
};
export const BaseManagePlans = observer(({ accounts, pangolin }: BaseManagePlansProps) => {
  return (
    PLATFORM === 'web' ? (
      <BaseManagePlansDefault
        accounts={accounts}
        pangolin={pangolin}
      />
    ) : (
      <BaseManagePlansDisabled/>
    )
  );
});

export const ManagePlans = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  return (
    <BaseManagePlans
      accounts={accounts}
      pangolin={pangolin}
    />
  );
});
